import {
    Background,
    Body,
    CFlex,
    CMargin,
    DText,
    Flex,
} from "../../../../styles/CommonStyles";
import Text from "../../../../core/utils/Text";
import Input from "../../../common/input/Input";
import ActionButton from "../../../common/buttons/ActionButton";
import CheckBox from "../../../common/input/CheckBox";
import { useEffect, useMemo, useState } from "react";
import { deepCopy, formatNumber } from "../../../../core/utils/common";
import { useQueryContext } from "../../../../core/contexts/query";
import { BiX } from "react-icons/bi";
import Tooltip from "../../../common/utils/Tooltip";
import { useAddPackage, useGetPackages } from "../../../../core/services/react-query/setting/package";
import Select from "../../../common/dropdown/Select";
import { useAvailableCoins } from "../../../../core/services/react-query/setting";

const initialPackage = {
    id: "",
    label: "",
    description: "",
    maxUse: 0,
    options: [],
    levelNumber: 1,
    priceAmount: "",
    priceCurrency: "usdt",
    priceAmountBeforeOff: "",
    expireDays: "",
    isActive: true,
};

const PackageModal = ({ onSuccess, type = "add", _package, existingPackages = [] }) => {
    console.log(existingPackages);

    const { setToast } = useQueryContext();
    const { data: packagesData } = useGetPackages();
    const { mutate: savePackages, isLoading } = useAddPackage(onSuccess, type); // PUT handler
    const { data: availableCoins } = useAvailableCoins()

    const [pkg, setPkg] = useState(initialPackage);
    const [currency, setCurrency] = useState(null)

    useEffect(() => {
        if (_package) setPkg(_package);
    }, [_package]);

    const onInputChange = (v, key) => {
        setPkg((s) => ({ ...s, [key]: v }));
    };

    const addOption = () => {
        setPkg((s) => ({ ...s, options: [...s.options, ""] }));
    };

    const removeOption = (index) => {
        const newOptions = deepCopy(pkg.options);
        newOptions.splice(index, 1);
        setPkg((s) => ({ ...s, options: newOptions }));
    };

    const updateOption = (index, value) => {
        const newOptions = deepCopy(pkg.options);
        newOptions[index] = value;
        setPkg((s) => ({ ...s, options: newOptions }));
    };

    // Validation
    const validAction = useMemo(() => {
        return !!pkg.label && !!pkg.priceAmount && !!pkg.levelNumber;
    }, [pkg]);

    const onAction = () => {
        if (!validAction) {
            setToast({ message: "fill-inputs-error", isError: true, show: true });
            return;
        }

        // Clean up + ensure correct structure
        const updatedPackage = {
            ...pkg,
            id: pkg.id || crypto.randomUUID(),
            maxUse: Number(pkg.maxUse),
            priceAmount: Number(pkg.priceAmount),
            priceAmountBeforeOff: Number(pkg.priceAmountBeforeOff) || 0,
            levelNumber: Number(pkg.levelNumber),
            expireDays: Number(pkg.expireDays) || 0,
        };

        let payload = [];

        if (type === "add") {
            // Add new package to the list
            payload = [...existingPackages, updatedPackage];
        } else if (type === "edit") {
            // Replace edited package
            payload = existingPackages.map((p) =>
                p.id === updatedPackage.id ? updatedPackage : p
            );
        } else if (type === "delete") {
            // Delete target package
            payload = existingPackages.filter((p) => p.id !== pkg.id);
        }

        savePackages(payload);
    };

    useEffect(() => {
        if (pkg) {
            let currency = availableCoins?.find(coin => coin.id === pkg.priceCurrency)
            currency && setCurrency(currency)
        }
    }, [pkg])


    const onCurrencyChange = (idx) => {
        setCurrency(availableCoins[idx])
        onInputChange(availableCoins[idx].id, 'priceCurrency')
    }


    return (
        <CFlex fw className="overflow-y-auto max-h-[calc(100vh-100px)] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            <DText main fontSize="b">
                <Text tid={`${type}-package`} />
            </DText>


            {type === "delete" ? (
                <>
                    <CMargin margin="20px" />
                    <DText main fontSize="m" color="red">
                        <Text tid="are-you-sure-delete" />: {pkg?.label}
                    </DText>

                    <CMargin margin="24px" />
                    <Flex justify="center" className="gap-5 w-full">
                        <ActionButton
                            width="100%"
                            height="42px"
                            onClick={onAction}
                            active
                            loading={isLoading}
                        >
                            <Text tid="confirm" />
                        </ActionButton>
                        <ActionButton
                            width="100%"
                            height="42px"
                            onClick={onSuccess}
                        >
                            <Text tid="cancel" />
                        </ActionButton>
                    </Flex>
                </>
            ) : (
                <>

                    <CMargin margin="8px" />
                    <Flex align="center" justify="start" style={{ marginTop: 8, width: "100%" }}>
                        <CheckBox
                            active={pkg.isActive}
                            onClick={() => setPkg((s) => ({ ...s, isActive: !s.isActive }))}
                        />
                        <DText main>
                            <Text tid="is-active" />
                        </DText>
                    </Flex>

                    <CMargin margin="8px" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 w-full">

                        <Background bg="mainBg" className={'lg:mt-[-10px]'}>
                            <Input
                                height="40px"
                                size="small"
                                value={pkg.label}
                                onInputChange={(v) => onInputChange(v, "label")}
                                label="Label"
                            />
                        </Background>
                        <Background bg="mainBg" className={'lg:mt-[-10px]'}>
                            <Input
                                height="40px"
                                size="small"
                                value={pkg.levelNumber}
                                onInputChange={(v) => onInputChange(v, "levelNumber")}
                                label="Level Number"
                            />
                        </Background>
                        <Background bg="mainBg" className={'lg:mt-[-10px]'}>
                            <Input
                                height="40px"
                                size="small"
                                value={pkg.expireDays}
                                onInputChange={(v) => onInputChange(v, "expireDays")}
                                label="Expire Days"
                            />
                        </Background>
                        <Select
                            options={availableCoins ? availableCoins : []}
                            value={currency}
                            onValueChange={onCurrencyChange}
                            placeHolder={'select-currency'}
                            fontSize={'s'}
                            width={'100%'}
                            height={'42px'}
                            isCoin
                        />
                        <Background bg="mainBg" className={'lg:mt-[-10px]'}>
                            <Input
                                height="40px"
                                size="small"
                                value={pkg.priceAmount}
                                onInputChange={(v) => onInputChange(v, "priceAmount")}
                                label="Price Amount"
                            />
                        </Background>
                        <Background bg="mainBg" className={'lg:mt-[-10px]'}>
                            <Input
                                height="40px"
                                size="small"
                                value={pkg.priceAmountBeforeOff}
                                onInputChange={(v) => onInputChange(v, "priceAmountBeforeOff")}
                                label="Price Before Discount"
                            />
                        </Background>
                        <Background bg="mainBg" className={'lg:mt-[-10px] md:col-span-2'}>
                            <Input
                                height="40px"
                                size="small"
                                value={pkg.maxUse}
                                onInputChange={(v) => onInputChange(v, "maxUse")}
                                label="Max Usage"
                            />
                        </Background>
                        <Background bg="mainBg" className={'lg:mt-[-10px] md:col-span-2'}>
                            <Input
                                height="40px"
                                size="small"
                                value={pkg.description}
                                onInputChange={(v) => onInputChange(v, "description")}
                                label="Description"
                            />
                        </Background>


                    </div>


                    <CMargin margin="8px" />
                    {
                        pkg.options.length > 0 ?
                            <DText main>Options</DText> : null
                    }
                    <div className="w-full grid md:grid-cols-2 gap-x-1">
                        {pkg.options.map((opt, index) => (
                            <Flex key={index} align="center" gap="8px">
                                <Background bg="mainBg" >
                                    <Input

                                        size="small"
                                        height="40px"
                                        value={opt}
                                        onInputChange={(v) => updateOption(index, v)}
                                        label={`Option ${index + 1}`}
                                    />
                                </Background>
                                <Tooltip content="remove-option">
                                    <button
                                        onClick={() => removeOption(index)}
                                        style={{ border: "none", background: "transparent", cursor: "pointer", marginBottom: '5px' }}
                                    >
                                        <BiX color="#c33" size={24} />
                                    </button>
                                </Tooltip>
                            </Flex>
                        ))}
                    </div>
                    <ActionButton height="36px" onClick={addOption} active>
                        Add Option
                    </ActionButton>

                    <CMargin margin="10px" className="!mt-auto" />
                    <ActionButton
                        active={validAction}
                        height="42px"
                        onClick={onAction}
                        loading={isLoading}
                    >
                        <Text tid="submit" />
                    </ActionButton>
                </>
            )}

        </CFlex>
    );
};

export default PackageModal;
